import { z } from 'zod'
import { workflowOutputSchema } from '@tunasong/ws-protocol'
import type { WorkflowOutput } from '@tunasong/ws-protocol'
import baseQuery from './base-query.js'
import { createApi } from '@reduxjs/toolkit/query/react'

/** The shared schemas live in the React-free `@tunasong/ws-protocol` so Lambdas need not load the store (GH #1916). */
export { workflowInputSchema, workflowOutputSchema } from '@tunasong/ws-protocol'
export type { WorkflowInput, WorkflowOutput } from '@tunasong/ws-protocol'

export const exportWorkflowOutputSchema = workflowOutputSchema.extend({
  // The signed S3 URL is only populated when the workflow has SUCCEEDED.
  // RUNNING / FAILED / etc. carry `url: null` over the wire.
  url: z.string().nullable(),
  /** Step Functions' short error code for a terminal failure (spec 2026-09-09 §6) */
  error: z.string().describe('Step Functions error code').nullable().optional(),
  /** The raw cause — ECS JSON; support material, never rendered to the user */
  cause: z.string().describe('Step Functions failure cause').nullable().optional(),
})
export type ExportWorkflowOutput = z.infer<typeof exportWorkflowOutputSchema>

// Define a service using a base URL and expected endpoints
export const workflowsApi = createApi({
  reducerPath: 'workflows-api',
  baseQuery,
  tagTypes: ['ExportWorkflows', 'ImportEnexWorkflows', 'ImportSpaceWorkflows'],
  endpoints: builder => ({
    export: builder.mutation<void, { rootEntityId?: string } | void>({
      invalidatesTags: ['ExportWorkflows'],
      query: params => ({
        method: 'POST',
        url: `workflows/export`,
        body: params ?? {},
      }),
    }),
    importEnex: builder.mutation<
      void,
      {
        name: string
        parentId?: string
        enexStorageKey: string
      }
    >({
      invalidatesTags: ['ImportEnexWorkflows'],
      query: params => ({
        method: 'POST',
        url: `workflows/import-enex`,
        body: params,
      }),
    }),
    getExports: builder.query<ExportWorkflowOutput[], void>({
      providesTags: ['ExportWorkflows'],
      query: () => `workflows/export`,
    }),
    /**
     * Import a library the client has ALREADY uploaded: it presigns
     * `users/<uid>/workflows/import-space/<jobId>.zip`, PUTs the zip there, and sends only
     * the key — the same two-step receive `importEnex` uses, so no archive goes through the
     * API. The backend refuses a key outside the caller's own upload area.
     */
    importSpace: builder.mutation<void, { zipStorageKey: string }>({
      invalidatesTags: ['ImportSpaceWorkflows'],
      query: params => ({
        method: 'POST',
        url: `workflows/import-space`,
        body: params,
      }),
    }),
    /** Running and finished imports for the caller, with status, error and cause */
    getImports: builder.query<WorkflowOutput[], void>({
      providesTags: ['ImportSpaceWorkflows'],
      query: () => `workflows/import-space`,
    }),
  }),
})
